import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    isOpen: boolean;
    onClose: () => void;
    id: string;
    extendedStyles?: PromoCodeModalStyles;
    promoMessage: string;
    promoCodeValue: string;
    promoButton: string;
}

type Position = "left" | "top" | "right" | "bottom";

export interface baseDrawerStyles {
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    wrapper: InjectableCss;
    position?: Position;
}

const baseDrawerStyles: DrawerPresentationProps = {
    size: 200,
    closeButtonProps: {
        shape: "rectangle",
        buttonType: "solid",
        color: "white",
        size: 50,
    },
    closeButtonIconProps: {
        src: "X",
        size: 20,
    },
    cssOverrides: {
        drawerTitle: css`
            background: white;
        `,
        drawerContent: css`
            max-height: 550px;
            overflow-y: inherit;
            radius: 6px;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            position: absolute;
            overflow: inherit;
        `,
        drawerContainer: css`
            position: relative;
            left: 50%;
            top: 25%;
            transform: translateX(-50%);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 500px;
                    `,
                    css`
                        width: 500px;
                    `,
                    css`
                        width: 500px;
                    `,
                ]);
            }}
        `,
    },
    position: "top",
};

export interface PromoCodeModalStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    contentGridItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    formWrapper?: InjectableCss;
    btnGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    textFieldGridItem?: GridItemProps;
    promoCodeTextField?: TextFieldPresentationProps;
    miscGridItem: GridItemProps;
}

export const promoCodeModalStyles: PromoCodeModalStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background-color: ${getColor("common.background")};
            border-radius: 8px;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 10px;
                    `,
                    css`
                        padding-right: 10px;
                        padding-bottom: 30px;
                    `,
                    css`
                        padding-right: 10px;
                        padding-bottom: 30px;
                    `,
                    css`
                        padding-right: 10px;
                        padding-bottom: 30px;
                    `,
                    css`
                        padding-right: 10px;
                        padding-bottom: 30px;
                    `,
                ]);
            }}
        `,
    },
    container: {
        gap: 10,
    },
    contentGridItem: {
        width: 12,
    },
    innerContainer: {
        gap: 20,
        css: css`
            padding-left: 12px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
        `,
    },
    textFieldGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 20px;
            background: ${getColor("common.accent")};
        `,
    },
    promoCodeTextField: {
        css: css`
            text-align: center;
        `,
    },
    btnGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
            width: 350px;
        `,
    },
    submitButton: {
        css: css`
            width: 68%;
            color: black;
        `,
    },
    miscGridItem: {
        width: 12,
        css: css`
            width: 400px;
        `,
    },
};

type Props = OwnProps;

const PromoCodeModal: React.FC<OwnProps> = ({
    id,
    isOpen,
    onClose,
    promoMessage,
    promoCodeValue,
    promoButton,
}: Props) => {
    return (
        <Drawer {...baseDrawerStyles} isOpen={isOpen} handleClose={onClose}>
            <StyledWrapper {...promoCodeModalStyles.wrapper}>
                <GridContainer {...promoCodeModalStyles.container}>
                    <GridItem {...promoCodeModalStyles.contentGridItem}>
                        <GridContainer {...promoCodeModalStyles.innerContainer}>
                            <GridItem {...promoCodeModalStyles.miscGridItem}>
                                <Typography>{parse(promoMessage, parserOptions)}</Typography>
                            </GridItem>
                            <StyledWrapper data-test-selector="form-wrapper" {...promoCodeModalStyles.formWrapper}>
                                <GridItem {...promoCodeModalStyles.textFieldGridItem}>
                                    <Typography>{parse(promoCodeValue, parserOptions)}</Typography>
                                </GridItem>
                                <GridItem {...promoCodeModalStyles.btnGridItem}>
                                    <Button {...promoCodeModalStyles.submitButton} onClick={onClose}>
                                        {parse(promoButton, parserOptions)}
                                    </Button>
                                </GridItem>
                            </StyledWrapper>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </Drawer>
    );
};

export default PromoCodeModal;
